import React, { FormEvent, useContext, useEffect, useState } from 'react';
import * as moment from 'moment';
import { IDetailData } from '../detail-data';
import { Col, ControlLabel, FormControl, Glyphicon, HelpBlock, Panel } from 'react-bootstrap';
import { PanelBodyWrapper, FormField } from '../styles';
import { useFormContext, Controller } from 'react-hook-form';
import { PatientIntakeContext } from '../context';
import MaskedInput from 'react-maskedinput';

interface IProps {
    disableEdit: boolean,
}

const PersonalInformation = ({ disableEdit }: IProps) => {
    const context = useContext(PatientIntakeContext);
    const [open, setOpen] = useState(true);

    const { control, formState: { errors } } = useFormContext<IDetailData>();

    return (
        <Panel expanded={open}>
            <Panel.Heading style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Panel.Title style={{ fontSize: 14, cursor: 'default' }} onClick={() => setOpen(!open)}>Personal Information</Panel.Title>
                <Panel.Toggle onClick={() => setOpen(!open)}><Glyphicon glyph={open ? 'triangle-bottom' : 'triangle-top'} /></Panel.Toggle>
            </Panel.Heading>
            <Panel.Collapse>
                <PanelBodyWrapper>
                    <FormField validationState={errors.firstName != null ? 'error' : null}>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>First Name: *</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='firstName'
                                rules={{
                                    required: 'First name is required'
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        type='text'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={onChange}
                                    />
                                )} />

                            {errors.firstName != null &&
                                <HelpBlock>{errors.firstName.message}</HelpBlock>
                            }
                        </Col>
                    </FormField>
                    <FormField validationState={errors.lastName != null ? 'error' : null}>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Last Name: *</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='lastName'
                                rules={{
                                    required: 'Last name is required'
                                }}
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        type='text'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={onChange}
                                    />
                                )} />

                            {errors.lastName != null &&
                                <HelpBlock>{errors.lastName.message}</HelpBlock>
                            }
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Preferred Name:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='preferredName'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        type='text'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={onChange}
                                    />
                                )} />
                        </Col>
                    </FormField>
                    <FormField validationState={errors.birthDate != null ? 'error' : null}>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Birth Date:</Col>
                        <Col xs={12} lg={8}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: 5 }}>
                                    <Controller
                                        control={control}
                                        name='birthDate'
                                        rules={{
                                            validate: (value) => {
                                                if (value) {
                                                    let birthDate = moment(value, 'MM/DD/YYYY', true);

                                                    if (!birthDate.isValid()) {
                                                        return 'Invalid birth date format';
                                                    } else if (birthDate.isAfter(moment())) {
                                                        return 'Birth date cannot be in the future';
                                                    } else if (birthDate.isBefore(moment('1901-01-01'))) {
                                                        return 'Birth date must be after 1901';
                                                    }
                                                }

                                                return true;
                                            }
                                        }}
                                        render={({ field: { onChange, value } }) => (
                                            <MaskedInput
                                                mask='11/11/1111'
                                                placeholder='MM/DD/YYYY'
                                                className='form-control'
                                                disabled={disableEdit}
                                                value={value || ''}
                                                onChange={onChange} />
                                        )} />
                                </div>
                            </div>

                            {errors.birthDate != null &&
                                <HelpBlock>{errors.birthDate.message}</HelpBlock>
                            }
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Gender:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='genderId'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={1}>Male</option>
                                        <option value={2}>Female</option>
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Preferred Language:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='preferredLanguageId'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={1}>English</option>
                                        <option value={2}>Other</option>
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Ethnicity:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='ethnicity'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={-1}>- Select -</option>
                                        {context.ethnicityList.map(item =>
                                            <option key={'ethnicity-' + item.id} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Marital Status:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='maritalStatus'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={-1}>- Select -</option>
                                        {context.maritalStatusList.map(item =>
                                            <option key={'marital-status-' + item.id} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Employment Status:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='employmentStatus'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={-1}>- Select -</option>
                                        {context.employmentStatusList.map(item =>
                                            <option key={'employment-status-' + item.id} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Occupation:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='occupation'
                                render={({ field: { onChange, value, ref } }) => (
                                    <FormControl
                                        type='text'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={onChange}
                                    />
                                )} />
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Highest Level of Education:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='educationLevel'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={-1}>- Select -</option>
                                        {context.educationLevelList.map(item =>
                                            <option key={'education-level-' + item.id} value={item.id}>{item.description}</option>
                                        )}
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                    <FormField>
                        <Col xs={12} lg={4} componentClass={ControlLabel}>Has Health Insurance:</Col>
                        <Col xs={12} lg={8}>
                            <Controller
                                control={control}
                                name='hasHealthInsurance'
                                render={({ field: { onChange, value } }) => (
                                    <FormControl
                                        componentClass='select'
                                        disabled={disableEdit}
                                        value={value}
                                        onChange={(e: FormEvent<FormControl & HTMLSelectElement>) => onChange(parseInt(e.currentTarget.value))}
                                    >
                                        <option value={-1}>- Select -</option>
                                        <option value={1}>Yes</option>
                                        <option value={0}>No</option>
                                    </FormControl>
                                )} />
                        </Col>
                    </FormField>
                </PanelBodyWrapper>
            </Panel.Collapse>
        </Panel>
    );
}

export default PersonalInformation;